/**
 * Pure error classification for subscription errors (no Amplify or other deps).
 * Used by subscription-manager and tested in isolation.
 */

/**
 * Normalize error message from multiple shapes (Error, { message }, Apollo/GraphQL { errors }).
 * Used so token/network checks can match messages from subscription error callbacks.
 */
export const getNormalizedErrorMessage = (error: unknown): string => {
  if (error == null) return "";
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  const o = error as {
    message?: string;
    errors?: Array<{ message?: string }>;
  };
  if (typeof o.message === "string" && o.message) return o.message;
  if (Array.isArray(o.errors) && o.errors[0] && typeof o.errors[0].message === "string") {
    return o.errors[0].message;
  }
  return "";
};

/**
 * Token/auth error patterns (recoverable: reconnect with fresh token).
 */
const TOKEN_ERROR_PATTERNS = [
  "unauthorized",
  "token expired",
  "authentication",
  "401",
  "403",
  "invalid token",
  "expired",
];

/**
 * Network/connection error patterns (recoverable: reconnect after blackout).
 */
const NETWORK_ERROR_PATTERNS = [
  "network",
  "connection",
  "fetch",
  "timeout",
  "websocket",
  "disconnect",
  "econnreset",
  "etimedout",
  "failed to fetch",
  "connection closed",
  "connection lost",
  "connection refused",
  "network error",
  "load failed",
];

/**
 * Helper to detect if an error is related to token expiration/authentication
 */
export const isTokenExpiredError = (error: unknown): boolean => {
  const msg = getNormalizedErrorMessage(error).toLowerCase();
  return TOKEN_ERROR_PATTERNS.some((p) => msg.includes(p));
};

/**
 * Returns true if the subscription error is recoverable (reconnect without surfacing to user).
 * Includes token/auth and network/connection errors.
 */
export const isRecoverableSubscriptionError = (error: unknown): boolean => {
  const msg = getNormalizedErrorMessage(error).toLowerCase();
  if (!msg) return false;
  return (
    TOKEN_ERROR_PATTERNS.some((p) => msg.includes(p)) ||
    NETWORK_ERROR_PATTERNS.some((p) => msg.includes(p))
  );
};
